import React, { useState, useEffect } from 'react';
import { updateProfile, uploadProfileImage } from '../../services/profileService';
import Combobox from '../common/Combobox';
import PlacesAutocomplete from '../common/PlacesAutocomplete';
import './ProfileForm.css';

// Import validation utilities
import { isValidEmail, isValidPhone, isValidUrl, isValidSocialMediaUrl } from '../../utils/validation';

// Data for dropdown options
import instrumentsData from '../../data/instrumentsData';
import professionsData from '../../data/professionsData';
import genresData from '../../data/genresData';
import { availabilityOptions, venueTypes } from '../../data/otherOptionsData';

// Required field component
const RequiredField = () => <span className="text-danger">*</span>;

/**
 * ProfileForm component for creating and editing user profiles
 * Supports both artist and venue profile types
 */
const ProfileForm = ({ initialData, userId, onSuccess, onCancel, isRequired = false }) => {
  // Common fields for both profile types
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [locationData, setLocationData] = useState(null);
  const [website, setWebsite] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');

  // Social media links
  const [instagramUrl, setInstagramUrl] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [tiktokUrl, setTiktokUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [bandcampUrl, setBandcampUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  
  // Artist-specific fields
  const [bandName, setBandName] = useState('');
  const [artistInstruments, setArtistInstruments] = useState([]);
  const [artistGenres, setArtistGenres] = useState([]);
  const [hourlyRate, setHourlyRate] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [availability, setAvailability] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [paymentPreference, setPaymentPreference] = useState('');
  
  // Venue-specific fields
  const [venueName, setVenueName] = useState('');
  const [venueType, setVenueType] = useState('');
  const [organizerTitle, setOrganizerTitle] = useState('');
  const [capacity, setCapacity] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [newAmenity, setNewAmenity] = useState('');
  const [bookingRequirements, setBookingRequirements] = useState('');
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userType, setUserType] = useState('artist');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Validation functions
  const validateName = (name) => {
    if (!name || name.trim() === '') return 'This field is required';
    if (name.length < 2) return 'Name must be at least 2 characters';
    return null;
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') return 'Email is required';
    if (!isValidEmail(email)) return 'Please enter a valid email address';
    return null;
  };

  const validatePhone = (phone) => {
    if (phone && !isValidPhone(phone)) return 'Please enter a valid phone number';
    return null;
  };

  const validateUrl = (url) => {
    if (url && !isValidUrl(url)) return 'Please enter a valid URL';
    return null;
  };

  const validateNumber = (value, min, fieldName) => {
    if (value === '') return null; // Allow empty values
    const num = parseFloat(value);
    if (isNaN(num)) return `${fieldName} must be a number`;
    if (num < min) return `${fieldName} must be at least ${min}`;
    return null;
  };

  const validateVenueName = (name) => {
    if (userType === 'venue' && (!name || name.trim() === '')) {
      return 'Venue name is required';
    }
    return null;
  };

  const validateVenueType = (type) => {
    if (userType === 'venue' && (!type || type === '')) {
      return 'Please select a venue type';
    }
    return null;
  };

  const validateProfessions = (selected) => {
    if (userType === 'artist' && (!selected || selected.length === 0)) {
      return 'Please select at least one profession';
    }
    return null;
  };

  // Generic input handler with validation
  const handleInputChange = (setter, value, validator, fieldName) => {
    setter(value);
    
    // Only show validation errors after first submission attempt
    // or if the field has been touched
    if (formSubmitted || validationErrors[fieldName]) {
      const error = validator ? validator(value) : null;
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
    }
  };

  // Validate all fields
  const validateForm = () => {
    const errors = {
      firstName: validateName(firstName),
      lastName: validateName(lastName),
      contactEmail: validateEmail(contactEmail),
      phone: validatePhone(phone),
      website: validateUrl(website),
      instagramUrl: instagramUrl ? isValidSocialMediaUrl(instagramUrl, 'instagram') ? null : 'Please enter a valid Instagram URL' : null,
      facebookUrl: facebookUrl ? isValidSocialMediaUrl(facebookUrl, 'facebook') ? null : 'Please enter a valid Facebook URL' : null,
      tiktokUrl: tiktokUrl ? isValidSocialMediaUrl(tiktokUrl, 'tiktok') ? null : 'Please enter a valid TikTok URL' : null,
      youtubeUrl: youtubeUrl ? isValidSocialMediaUrl(youtubeUrl, 'youtube') ? null : 'Please enter a valid YouTube URL' : null,
      soundcloudUrl: soundcloudUrl ? isValidSocialMediaUrl(soundcloudUrl, 'soundcloud') ? null : 'Please enter a valid SoundCloud URL' : null,
      bandcampUrl: bandcampUrl ? isValidSocialMediaUrl(bandcampUrl, 'bandcamp') ? null : 'Please enter a valid Bandcamp URL' : null,
      spotifyUrl: spotifyUrl ? isValidSocialMediaUrl(spotifyUrl, 'spotify') ? null : 'Please enter a valid Spotify URL' : null,
      hourlyRate: validateNumber(hourlyRate, 0, 'Hourly rate'),
      capacity: validateNumber(capacity, 0, 'Capacity'),
      bio: validateName(bio),
    };

    // User type specific validations
    if (userType === 'artist') {
      errors.professions = validateProfessions(professions);
      // Only validate bandName for artists
      errors.bandName = validateName(bandName);
    } else if (userType === 'venue') {
      errors.venueName = validateVenueName(venueName);
      errors.venueType = validateVenueType(venueType);
      // Only validate organizerTitle for venues
      errors.organizerTitle = validateName(organizerTitle);
    }

    // Filter out null errors
    const filteredErrors = Object.fromEntries(
      Object.entries(errors).filter(([_, value]) => value !== null)
    );

    setValidationErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setFormSubmitted(true);
    
    // Validate all fields before submission
    if (!validateForm()) {
      console.log('Validation errors:', validationErrors);
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    console.log('Form submitted');
    
    setLoading(true);
    setError(null);
    setSubmitting(true);
    setSubmitMessage('Saving profile...');
    
    try {
      console.log('Submitting profile form for user ID:', userId);
      console.log('User type:', userType);
      console.log('Validation passed');
      
      // Prepare profile data
      const profileData = {
        // Common fields
        first_name: firstName,
        last_name: lastName,
        full_name: `${firstName} ${lastName}`,
        bio,
        contact_email: contactEmail,
        phone,
        location: location, // Store the full formatted address
        website: website,
        user_type: userType,
      };

      // Add social media links to profileData
      profileData.instagram_url = instagramUrl;
      profileData.facebook_url = facebookUrl;
      profileData.tiktok_url = tiktokUrl;
      profileData.youtube_url = youtubeUrl;
      profileData.soundcloud_url = soundcloudUrl;
      profileData.bandcamp_url = bandcampUrl;
      profileData.spotify_url = spotifyUrl;
      
      // Store location coordinates for all users
      // These will be used differently based on user type:
      // - For venues: Displayed publicly on maps
      // - For artists: Used only for radius search, never displayed publicly
      if (locationData && locationData.lat && locationData.lng) {
        profileData.location_lat = locationData.lat;
        profileData.location_lng = locationData.lng;
      }
      
      console.log('Profile data being sent to Supabase:', profileData);
      
      // Add type-specific fields
      if (userType === 'artist') {
        console.log('Adding artist-specific fields');
        profileData.band_name = bandName;
        profileData.instruments = artistInstruments;
        profileData.genres = artistGenres;
        profileData.hourly_rate = hourlyRate ? parseFloat(hourlyRate) : null;
        profileData.years_of_experience = yearsOfExperience ? parseInt(yearsOfExperience, 10) : null;
        profileData.experience_level = experienceLevel;
        profileData.availability = availability;
        profileData.professions = professions;
        profileData.payment_preference = paymentPreference;
      } else if (userType === 'venue') {
        console.log('Adding venue-specific fields');
        console.log('Venue name:', venueName);
        profileData.venue_name = venueName;
        profileData.venue_type = venueType;
        profileData.organizer_title = organizerTitle;
        profileData.capacity = capacity ? parseInt(capacity, 10) : null;
        profileData.amenities = amenities;
        profileData.booking_requirements = bookingRequirements;
      }
      
      // Handle profile image upload if there's a new image
      if (profileImage) {
        setSubmitMessage('Uploading profile image...');
        
        try {
          console.log('Uploading new profile image...');
          const { url, error: uploadError } = await uploadProfileImage(userId, profileImage);
          
          if (uploadError) {
            console.error('Profile image upload error:', uploadError);
            setError(`Failed to upload image: ${uploadError.message || 'Network error'}`);
            setSubmitting(false);
            setLoading(false);
            return;
          }
          
          if (url) {
            console.log('Profile image uploaded successfully. URL:', url);
            profileData.profile_image_url = url;
          } else {
            console.warn('No URL returned from image upload');
            setError('Image upload completed but no URL was returned. Please try again.');
            setSubmitting(false);
            setLoading(false);
            return;
          }
        } catch (imageError) {
          console.error('Unexpected error during image upload:', imageError);
          setError(`Image upload failed: ${imageError.message || 'Unknown error'}`);
          setSubmitting(false);
          setLoading(false);
          return;
        }
      } else if (profileImageUrl && profileImageUrl.startsWith('data:')) {
        // If we have a data URL but no file object, convert it to a file and upload
        setSubmitMessage('Processing and uploading image...');
        console.log('Converting data URL to file and uploading...');
        
        try {
          const blob = await fetch(profileImageUrl).then(r => r.blob());
          const file = new File([blob], 'profile-image.jpg', { type: 'image/jpeg' });
          const { url, error: uploadError } = await uploadProfileImage(userId, file);
          
          if (uploadError) {
            console.error('Data URL image upload error:', uploadError);
            setError(`Failed to upload image: ${uploadError.message || 'Network error'}`);
            setSubmitting(false);
            setLoading(false);
            return;
          }
          
          if (url) {
            console.log('Data URL image uploaded successfully. URL:', url);
            profileData.profile_image_url = url;
          } else {
            console.warn('No URL returned from data URL image upload');
          }
        } catch (conversionError) {
          console.error('Error converting data URL:', conversionError);
          setError(`Failed to process image: ${conversionError.message || 'Format error'}`);
          setSubmitting(false);
          setLoading(false);
          return;
        }
      } else {
        console.log('No new profile image to upload');
      }
      
      // Update profile
      console.log('Updating profile with data:', profileData);
      const { data, error: updateError } = await updateProfile(userId, profileData);
      
      if (updateError) {
        console.error('Profile update error:', updateError);
        setError(`Failed to update profile: ${updateError.message || 'Network error'}`);
        setSubmitting(false);
        setLoading(false);
        return;
      }
      
      console.log('Profile updated successfully:', data);
      
      // Call the success callback with the updated profile
      console.log('Calling success callback with updated profile');
      onSuccess({ ...profileData, id: userId });
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(`An unexpected error occurred: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };
  
  // Initialize form with existing data if available
  useEffect(() => {
    if (initialData) {
      console.log('Initializing form with data:', initialData);
      
      // Common fields
      setFirstName(initialData.first_name || '');
      setLastName(initialData.last_name || '');
      setFullName(initialData.full_name || '');
      setBio(initialData.bio || '');
      setContactEmail(initialData.contact_email || '');
      setPhone(initialData.phone || '');
      setLocation(initialData.location || '');
      setWebsite(initialData.website || '');
      setProfileImageUrl(initialData.profile_image_url || '');
      setUserType(initialData.user_type || 'artist');

      // Social media links
      setInstagramUrl(initialData.instagram_url || '');
      setFacebookUrl(initialData.facebook_url || '');
      setTiktokUrl(initialData.tiktok_url || '');
      setYoutubeUrl(initialData.youtube_url || '');
      setSoundcloudUrl(initialData.soundcloud_url || '');
      setBandcampUrl(initialData.bandcamp_url || '');
      setSpotifyUrl(initialData.spotify_url || '');
      
      // Artist-specific fields
      if (initialData.band_name) setBandName(initialData.band_name);
      if (initialData.instruments) setArtistInstruments(initialData.instruments);
      if (initialData.genres) setArtistGenres(initialData.genres);
      if (initialData.hourly_rate) setHourlyRate(initialData.hourly_rate.toString());
      if (initialData.years_of_experience) setYearsOfExperience(initialData.years_of_experience.toString());
      if (initialData.experience_level) setExperienceLevel(initialData.experience_level);
      if (initialData.availability) setAvailability(initialData.availability);
      if (initialData.professions) setProfessions(initialData.professions);
      if (initialData.payment_preference) setPaymentPreference(initialData.payment_preference);
      
      // Venue-specific fields
      if (initialData.venue_name) setVenueName(initialData.venue_name);
      if (initialData.venue_type) setVenueType(initialData.venue_type);
      if (initialData.organizer_title) setOrganizerTitle(initialData.organizer_title);
      if (initialData.capacity) setCapacity(initialData.capacity.toString());
      if (initialData.amenities) setAmenities(initialData.amenities);
      if (initialData.booking_requirements) setBookingRequirements(initialData.booking_requirements);
    }
  }, [initialData]);
  
  // Handle adding items to arrays (amenities)
  const handleAddAmenity = () => {
    if (newAmenity.trim() && !amenities.includes(newAmenity.trim())) {
      setAmenities([...amenities, newAmenity.trim()]);
      setNewAmenity('');
    }
  };
  
  // Handle removing items from arrays
  const handleRemoveAmenity = (index) => {
    setAmenities(amenities.filter((_, i) => i !== index));
  };
  
  // Handle profile image selection
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      console.log('Selected file:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file is too large. Maximum size is 5MB.');
        return;
      }
      
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please upload a JPEG or PNG image.');
        return;
      }
      
      setProfileImage(file);
      setError(null);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result;
        console.log('Created preview URL:', previewUrl.substring(0, 50) + '...');
        setProfileImageUrl(previewUrl);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Get missing required fields
  const getMissingRequiredFields = () => {
    const missing = [];
    if (!firstName?.trim()) missing.push('First Name');
    if (!lastName?.trim()) missing.push('Last Name');
    if (userType === 'artist') {
      if (!professions || professions.length === 0) missing.push('Profession(s)');
    }
    if (userType === 'venue') {
      if (!venueName?.trim()) missing.push('Venue Name');
    }
    return missing;
  };
  const missingRequiredFields = getMissingRequiredFields();
  
  return (
    <div className="profile-form-container">
      <div className="profile-form">
        <div className="profile-form-header">
          <h2>{initialData ? 'Edit Your Profile' : 'Create Your Profile'}</h2>
          <p>Complete your profile to increase your visibility and connect with others</p>
        </div>
        
        <div className="profile-form-body">
          {error && (
            <div className="error-message">
              <i className="bi bi-exclamation-triangle-fill"></i>
              {error}
            </div>
          )}
          
          {/* Show missing required fields summary */}
          {missingRequiredFields.length > 0 && (
            <div className="alert alert-warning">
              <h5>Complete your profile</h5>
              <p>Please fill in the following required fields to continue:</p>
              <ul className="mb-0">
                {missingRequiredFields.map((field, index) => (
                  <li key={index}>
                    <a href={`#${field.toLowerCase().replace(/\s+/g, '-')}`} className="text-decoration-none">
                      {field}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
          {/* Profile Type Selection */}
          <div className="form-section">
            <h3 className="form-section-title"><i className="bi bi-person-badge"></i> Profile Type</h3>
            <div className="profile-type-selector">
              <div 
                className={`profile-type-option ${userType === 'artist' ? 'selected' : ''}`}
                onClick={() => {
                  if (!(initialData && initialData.user_type)) {
                    console.log('Changing user type to artist');
                    setUserType('artist');
                  }
                }}
              >
                <i className="bi bi-music-note-beamed"></i>
                <h4>Artist / Musician</h4>
                <p>For performers, musicians, and artists</p>
                <input
                  type="radio"
                  name="userType"
                  id="artistType"
                  value="artist"
                  checked={userType === 'artist'}
                  onChange={() => {}}
                  style={{ display: 'none' }}
                  disabled={initialData && initialData.user_type}
                />
              </div>
              <div 
                className={`profile-type-option ${userType === 'venue' ? 'selected' : ''}`}
                onClick={() => {
                  if (!(initialData && initialData.user_type)) {
                    console.log('Changing user type to venue');
                    setUserType('venue');
                  }
                }}
              >
                <i className="bi bi-building"></i>
                <h4>Venue / Event Space</h4>
                <p>For venues, event spaces, and organizers</p>
                <input
                  type="radio"
                  name="userType"
                  id="venueType"
                  value="venue"
                  checked={userType === 'venue'}
                  onChange={() => {}}
                  style={{ display: 'none' }}
                  disabled={initialData && initialData.user_type}
                />
              </div>
            </div>
          </div>
          
          {/* Profile Image */}
          <div className="form-section">
            <h3 className="form-section-title"><i className="bi bi-person-circle"></i> Profile Image</h3>
            <div className="profile-image-upload">
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile Preview"
                  className="profile-image-preview"
                />
              ) : (
                <div className="profile-image-placeholder">
                  <span>
                    {fullName ? fullName.charAt(0).toUpperCase() : '?'}
                  </span>
                </div>
              )}
              <div className="upload-button-container">
                <label htmlFor="profileImage" className="custom-file-upload">
                  <i className="bi bi-cloud-arrow-up"></i> Choose Image
                </label>
                <input
                  type="file"
                  className="file-input"
                  id="profileImage"
                  accept="image/jpeg, image/png"
                  onChange={handleImageChange}
                />
                <div className="form-text">
                  <i className="bi bi-info-circle"></i> Max file size: 5MB. Accepted formats: JPEG, PNG.
                </div>
              </div>
            </div>
          </div>
          
          {/* Common Fields */}
          <div className="form-section">
            <h3 className="form-section-title"><i className="bi bi-person"></i> Personal Information</h3>
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="firstName" className="form-label required-field">First Name <RequiredField /></label>
                <input
                  type="text"
                  className={`form-control ${validationErrors.firstName ? 'is-invalid' : ''}`}
                  id="firstName"
                  value={firstName}
                  onChange={(e) => handleInputChange(setFirstName, e.target.value, validateName, 'firstName')}
                  required
                />
                {validationErrors.firstName && <div className="invalid-feedback">{validationErrors.firstName}</div>}
              </div>
              <div className="col-md-6">
                <label htmlFor="lastName" className="form-label required-field">Last Name <RequiredField /></label>
                <input
                  type="text"
                  className={`form-control ${validationErrors.lastName ? 'is-invalid' : ''}`}
                  id="lastName"
                  value={lastName}
                  onChange={(e) => handleInputChange(setLastName, e.target.value, validateName, 'lastName')}
                  required
                />
                {validationErrors.lastName && <div className="invalid-feedback">{validationErrors.lastName}</div>}
              </div>
            </div>
          
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="contactEmail" className="form-label">Contact Email</label>
                <div className="social-link-input">
                  <i className="bi bi-envelope"></i>
                  <input
                    type="email"
                    className={`form-control ${validationErrors.contactEmail ? 'is-invalid' : ''}`}
                    id="contactEmail"
                    value={contactEmail}
                    onChange={(e) => handleInputChange(setContactEmail, e.target.value, validateEmail, 'contactEmail')}
                  />
                  {validationErrors.contactEmail && <div className="invalid-feedback">{validationErrors.contactEmail}</div>}
                </div>
              </div>
              <div className="col-md-6">
                <label htmlFor="phone" className="form-label">Phone Number</label>
                <div className="social-link-input">
                  <i className="bi bi-telephone"></i>
                  <input
                    type="tel"
                    className={`form-control ${validationErrors.phone ? 'is-invalid' : ''}`}
                    id="phone"
                    value={phone}
                    onChange={(e) => handleInputChange(setPhone, e.target.value, validatePhone, 'phone')}
                  />
                  {validationErrors.phone && <div className="invalid-feedback">{validationErrors.phone}</div>}
                </div>
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="location" className="form-label">Location</label>
              <div className="social-link-input">
                <i className="bi bi-geo-alt"></i>
                <PlacesAutocomplete
                  id="location"
                  value={location}
                  onChange={(address, locationDetails) => {
                    console.log('Location selected:', address);
                    console.log('Location details:', locationDetails);
                    setLocation(address); // This is what gets saved to the 'location' column
                    setLocationData(locationDetails);
                  }}
                  placeholder="Search for a location"
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="websiteUrl" className="form-label">Website</label>
              <div className="social-link-input">
                <i className="bi bi-globe"></i>
                <input
                  type="url"
                  className={`form-control ${validationErrors.websiteUrl ? 'is-invalid' : ''}`}
                  id="website"
                  value={website}
                  onChange={(e) => handleInputChange(setWebsite, e.target.value, validateUrl, 'website')}
                  placeholder="https://yourwebsite.com"
                />
                {validationErrors.website && <div className="invalid-feedback">{validationErrors.website}</div>}
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="bio" className="form-label">Bio</label>
              <textarea
                className={`form-control ${validationErrors.bio ? 'is-invalid' : ''}`}
                id="bio"
                rows="4"
                value={bio}
                onChange={(e) => handleInputChange(setBio, e.target.value, null, 'bio')}
                placeholder="Tell us about yourself"
              ></textarea>
              {validationErrors.bio && <div className="invalid-feedback">{validationErrors.bio}</div>}
            </div>
          </div>
          
          {/* Artist-specific fields */}
          {userType === 'artist' && (
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-music-note-beamed"></i> Artist Information</h3>
              
              {/* Band Name */}
              <div className="mb-3">
                <label htmlFor="bandName" className="form-label required-field">Band/Artist Name <RequiredField /></label>
                <div className="social-link-input">
                  <i className="bi bi-people"></i>
                  <input
                    type="text"
                    className={`form-control ${validationErrors.bandName ? 'is-invalid' : ''}`}
                    id="bandName"
                    value={bandName}
                    onChange={(e) => handleInputChange(setBandName, e.target.value, validateName, 'bandName')}
                    placeholder="Enter band or artist name"
                  />
                  {validationErrors.bandName && <div className="invalid-feedback">{validationErrors.bandName}</div>}
                </div>
              </div>
              
              {/* Combobox fields in a 2x2 grid */}
              <div className="row">
                {/* First row: Professions and Instruments side by side */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="professions" className="form-label required-field">
                    Profession(s) <RequiredField />
                  </label>
                  <Combobox
                    options={professionsData}
                    selectedValues={professions}
                    onChange={(selected) => {
                      setProfessions(selected);
                      setValidationErrors(prev => ({
                        ...prev,
                        professions: validateProfessions(selected)
                      }));
                    }}
                    placeholder="Select your professions"
                    allowCustomValues={false}
                    multiple={true}
                  />
                  {validationErrors.professions && <div className="text-danger mt-1">{validationErrors.professions}</div>}
                  {isRequired && (
                    <div className="form-text">
                      <i className="bi bi-info-circle"></i> At least one profession is required.
                    </div>
                  )}
                  <div className="tag-container">
                    {professions.map((profession, index) => (
                      <span key={index} className="tag">
                        {profession}
                        <span 
                          className="tag-remove"
                          onClick={() => setProfessions(professions.filter((_, i) => i !== index))}
                          aria-label="Remove"
                        >
                          X
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Instruments - Next to Professions */}
                <div className="col-md-6 mb-3">
                  <label className="form-label required-field">Instrument(s) <RequiredField /></label>
                  <Combobox
                    id="instruments"
                    options={instrumentsData}
                    selectedValues={artistInstruments}
                    onChange={(selected) => {
                      setArtistInstruments(selected);
                      setValidationErrors(prev => ({
                        ...prev,
                        artistInstruments: null
                      }));
                    }}
                    placeholder="Add instruments you play"
                    allowCustomValues={true}
                    multiple={true}
                  />
                  {validationErrors.artistInstruments && <div className="text-danger mt-1">{validationErrors.artistInstruments}</div>}
                  <div className="tag-container">
                    {artistInstruments.map((instrument, index) => (
                      <span key={index} className="tag">
                        {instrument}
                        <span 
                          className="tag-remove"
                          onClick={() => setArtistInstruments(artistInstruments.filter((_, i) => i !== index))}
                          aria-label="Remove"
                        >
                          X
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Second row: Genres and Availability side by side */}
              <div className="row">
                {/* Genres */}
                <div className="col-md-6 mb-3">
                  <label className="form-label required-field">Genre(s) <RequiredField /></label>
                  <Combobox
                    id="genres"
                    options={genresData}
                    selectedValues={artistGenres}
                    onChange={(selected) => {
                      setArtistGenres(selected);
                      setValidationErrors(prev => ({
                        ...prev,
                        artistGenres: null
                      }));
                    }}
                    placeholder="Add genres you perform"
                    allowCustomValues={true}
                    multiple={true}
                  />
                  {validationErrors.artistGenres && <div className="text-danger mt-1">{validationErrors.artistGenres}</div>}
                  <div className="tag-container">
                    {artistGenres.map((genre, index) => (
                      <span key={index} className="tag">
                        {genre}
                        <span 
                          className="tag-remove"
                          onClick={() => setArtistGenres(artistGenres.filter((_, i) => i !== index))}
                          aria-label="Remove"
                        >
                          X
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Availability - Next to Genres */}
                <div className="col-md-6 mb-3">
                  <label className="form-label required-field">Availability <RequiredField /></label>
                  <Combobox
                    id="availability"
                    options={availabilityOptions}
                    selectedValues={availability}
                    onChange={(selected) => {
                      setAvailability(selected);
                      setValidationErrors(prev => ({
                        ...prev,
                        availability: null
                      }));
                    }}
                    placeholder="Add your availability"
                    allowCustomValues={true}
                    multiple={true}
                  />
                  {validationErrors.availability && <div className="text-danger mt-1">{validationErrors.availability}</div>}
                  <div className="tag-container">
                    {availability.map((time, index) => (
                      <span key={index} className="tag">
                        {time}
                        <span 
                          className="tag-remove"
                          onClick={() => setAvailability(availability.filter((_, i) => i !== index))}
                          aria-label="Remove"
                        >
                          X
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Venue-specific fields */}
          {userType === 'venue' && (
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-building"></i> Venue Information</h3>
              
              <div className="mb-3">
                <label htmlFor="venueName" className="form-label required-field">
                  Venue Name <RequiredField />
                </label>
                <div className="social-link-input">
                  <i className="bi bi-house"></i>
                  <input
                    type="text"
                    className={`form-control ${validationErrors.venueName ? 'is-invalid' : ''}`}
                    id="venueName"
                    value={venueName}
                    onChange={(e) => {
                      console.log('Setting venue name to:', e.target.value);
                      setVenueName(e.target.value);
                    }}
                    placeholder="Enter venue name"
                    required={userType === 'venue' && isRequired}
                  />
                  {validationErrors.venueName && <div className="invalid-feedback">{validationErrors.venueName}</div>}
                </div>
              </div>
              
              <div className="mb-3">
                <label htmlFor="venueType" className="form-label required-field">
                  Venue Type <RequiredField />
                </label>
                <select
                  className={`form-select ${validationErrors.venueType ? 'is-invalid' : ''}`}
                  id="venueType"
                  value={venueType}
                  onChange={(e) => handleInputChange(setVenueType, e.target.value, validateVenueType, 'venueType')}
                  required={userType === 'venue'}
                >
                  <option value="">Select venue type</option>
                  {venueTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {validationErrors.venueType && <div className="invalid-feedback">{validationErrors.venueType}</div>}
              </div>
              
              <div className="mb-3">
                <label htmlFor="organizerTitle" className="form-label required-field">Organizer Title <RequiredField /></label>
                <div className="social-link-input">
                  <i className="bi bi-person-badge"></i>
                  <input
                    type="text"
                    className={`form-control ${validationErrors.organizerTitle ? 'is-invalid' : ''}`}
                    id="organizerTitle"
                    value={organizerTitle}
                    onChange={(e) => handleInputChange(setOrganizerTitle, e.target.value, validateName, 'organizerTitle')}
                    placeholder="e.g. Booking Manager"
                    required={userType === 'venue'}
                  />
                  {validationErrors.organizerTitle && <div className="invalid-feedback">{validationErrors.organizerTitle}</div>}
                </div>
              </div>
              
              <div className="mb-3">
                <label htmlFor="capacity" className="form-label required-field">
                  Capacity <RequiredField />
                </label>
                <div className="social-link-input">
                  <i className="bi bi-people-fill"></i>
                  <input
                    type="number"
                    className={`form-control ${validationErrors.capacity ? 'is-invalid' : ''}`}
                    id="capacity"
                    min="0"
                    value={capacity}
                    onChange={(e) => handleInputChange(setCapacity, e.target.value, (val) => validateNumber(val, 0, 'Capacity'), 'capacity')}
                    placeholder="Maximum capacity"
                  />
                  {validationErrors.capacity && <div className="invalid-feedback">{validationErrors.capacity}</div>}
                </div>
              </div>
              
              {/* Amenities */}
              <div className="mb-3">
                <label className="form-label">Amenities</label>
                <div className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    placeholder="Add an amenity..."
                  />
                  <button
                    type="button"
                    className="btn-save"
                    style={{ padding: '8px 15px' }}
                    onClick={handleAddAmenity}
                  >
                    <i className="bi bi-plus-circle"></i> Add
                  </button>
                </div>
                {amenities.length > 0 && (
                  <div className="tag-container">
                    {amenities.map((amenity, index) => (
                      <span key={index} className="tag">
                        {amenity}
                        <span 
                          className="tag-remove"
                          onClick={() => handleRemoveAmenity(index)}
                          aria-label="Remove"
                        >
                          X
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="mb-3">
                <label htmlFor="bookingRequirements" className="form-label">Booking Requirements</label>
                <textarea
                  className="form-control"
                  id="bookingRequirements"
                  rows="4"
                  value={bookingRequirements}
                  onChange={(e) => setBookingRequirements(e.target.value)}
                  placeholder="Describe your venue's booking requirements..."
                ></textarea>
              </div>
            </div>
          )}
          
          {/* Social Links Section */}
          <div className="form-section">
            <h3 className="form-section-title"><i className="bi bi-share"></i> Social Links</h3>
            <div className="social-links-section">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="instagramUrl" className="form-label">Instagram</label>
                  <div className="social-link-input">
                    <i className="bi bi-instagram"></i>
                    <input
                      type="url"
                      className={`form-control ${validationErrors.instagramUrl ? 'is-invalid' : ''}`}
                      id="instagramUrl"
                      value={instagramUrl}
                      onChange={(e) => handleInputChange(setInstagramUrl, e.target.value, (url) => url ? isValidSocialMediaUrl(url, 'instagram') ? null : 'Please enter a valid Instagram URL' : null, 'instagramUrl')}
                      placeholder="https://instagram.com/yourusername"
                    />
                    {validationErrors.instagramUrl && <div className="invalid-feedback">{validationErrors.instagramUrl}</div>}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="facebookUrl" className="form-label">Facebook</label>
                  <div className="social-link-input">
                    <i className="bi bi-facebook"></i>
                    <input
                      type="url"
                      className={`form-control ${validationErrors.facebookUrl ? 'is-invalid' : ''}`}
                      id="facebookUrl"
                      value={facebookUrl}
                      onChange={(e) => handleInputChange(setFacebookUrl, e.target.value, (url) => url ? isValidSocialMediaUrl(url, 'facebook') ? null : 'Please enter a valid Facebook URL' : null, 'facebookUrl')}
                      placeholder="https://facebook.com/yourpage"
                    />
                    {validationErrors.facebookUrl && <div className="invalid-feedback">{validationErrors.facebookUrl}</div>}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="tiktokUrl" className="form-label">TikTok</label>
                  <div className="social-link-input">
                    <i className="bi bi-tiktok"></i>
                    <input
                      type="url"
                      className={`form-control ${validationErrors.tiktokUrl ? 'is-invalid' : ''}`}
                      id="tiktokUrl"
                      value={tiktokUrl}
                      onChange={(e) => handleInputChange(setTiktokUrl, e.target.value, (url) => url ? isValidSocialMediaUrl(url, 'tiktok') ? null : 'Please enter a valid TikTok URL' : null, 'tiktokUrl')}
                      placeholder="https://tiktok.com/@username"
                    />
                    {validationErrors.tiktokUrl && <div className="invalid-feedback">{validationErrors.tiktokUrl}</div>}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="youtubeUrl" className="form-label">YouTube</label>
                  <div className="social-link-input">
                    <i className="bi bi-youtube"></i>
                    <input
                      type="url"
                      className={`form-control ${validationErrors.youtubeUrl ? 'is-invalid' : ''}`}
                      id="youtubeUrl"
                      value={youtubeUrl}
                      onChange={(e) => handleInputChange(setYoutubeUrl, e.target.value, (url) => url ? isValidSocialMediaUrl(url, 'youtube') ? null : 'Please enter a valid YouTube URL' : null, 'youtubeUrl')}
                      placeholder="https://youtube.com/c/yourchannel"
                    />
                    {validationErrors.youtubeUrl && <div className="invalid-feedback">{validationErrors.youtubeUrl}</div>}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="soundcloudUrl" className="form-label">SoundCloud</label>
                  <div className="social-link-input">
                    <i className="bi bi-cloud"></i>
                    <input
                      type="url"
                      className={`form-control ${validationErrors.soundcloudUrl ? 'is-invalid' : ''}`}
                      id="soundcloudUrl"
                      value={soundcloudUrl}
                      onChange={(e) => handleInputChange(setSoundcloudUrl, e.target.value, (url) => url ? isValidSocialMediaUrl(url, 'soundcloud') ? null : 'Please enter a valid SoundCloud URL' : null, 'soundcloudUrl')}
                      placeholder="https://soundcloud.com/yourprofile"
                    />
                    {validationErrors.soundcloudUrl && <div className="invalid-feedback">{validationErrors.soundcloudUrl}</div>}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="bandcampUrl" className="form-label">Bandcamp</label>
                  <div className="social-link-input">
                    <i className="bi bi-music-note"></i>
                    <input
                      type="url"
                      className={`form-control ${validationErrors.bandcampUrl ? 'is-invalid' : ''}`}
                      id="bandcampUrl"
                      value={bandcampUrl}
                      onChange={(e) => handleInputChange(setBandcampUrl, e.target.value, (url) => url ? isValidSocialMediaUrl(url, 'bandcamp') ? null : 'Please enter a valid Bandcamp URL' : null, 'bandcampUrl')}
                      placeholder="https://yourbandname.bandcamp.com"
                    />
                    {validationErrors.bandcampUrl && <div className="invalid-feedback">{validationErrors.bandcampUrl}</div>}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="spotifyUrl" className="form-label">Spotify</label>
                  <div className="social-link-input">
                    <i className="bi bi-spotify"></i>
                    <input
                      type="url"
                      className={`form-control ${validationErrors.spotifyUrl ? 'is-invalid' : ''}`}
                      id="spotifyUrl"
                      value={spotifyUrl}
                      onChange={(e) => handleInputChange(setSpotifyUrl, e.target.value, (url) => url ? isValidSocialMediaUrl(url, 'spotify') ? null : 'Please enter a valid Spotify URL' : null, 'spotifyUrl')}
                      placeholder="https://open.spotify.com/artist/yourid"
                    />
                    {validationErrors.spotifyUrl && <div className="invalid-feedback">{validationErrors.spotifyUrl}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-buttons">
            <button
              type="button"
              className="btn-cancel"
              onClick={onCancel}
              disabled={loading || submitting}
            >
              <i className="bi bi-x-circle"></i> Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={loading || submitting}
              onClick={(e) => {
                console.log('Save button clicked');
                // Prevent default to avoid double submission
                e.preventDefault();
                handleSubmit(e);
              }}
            >
              {loading || submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {submitMessage || 'Saving...'}
                </>
              ) : (
                <><i className="bi bi-check-circle"></i> Save Profile</>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileForm;
